import {
    AdminAccountCreateRequest,
    AdminAccountDTO,
    AdminAccountItem,
    AdminImpersonateAuthTicketDTO,
    CustomizationCode,
    DefaultPasswordDTO,
    ORAPIClientConfigDTO,
    OrderListItem,
    SiteAuditLog,
    SiteDTO,
    SiteIntegrationMethodEnum,
    SiteIntegrationTypeEnum,
    SiteTypes
} from '@beable/roster-backend-client-v1';
import authService from './AuthService';
import RosterBackendService from './RosterBackendService';

async function getAllAdmins(active?: boolean): Promise<AdminAccountItem[]> {
    const response = await RosterBackendService.manageGetAll(!!active);
    if (response.isOk()) {
        return response.value;
    } else {
        return [];
    }
}

async function addNewAdmin(newAdmin: AdminAccountCreateRequest): Promise<AdminAccountDTO | null> {
    const response = await RosterBackendService.manageAddNewAdmin(newAdmin);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function editAdminPassword(password: string, id: string): Promise<boolean> {
    const response = await RosterBackendService.manageChangePassword({ password, id });
    if (response.isOk()) {
        return true;
    } else {
        return false;
    }
}

async function deleteAdmin(id: string, username: string): Promise<boolean> {
    const response = await RosterBackendService.manageDeleteAdmin2(id, username);
    if (response.isOk()) {
        return response.value;
    } else {
        return false;
    }
}

async function reactivateAdmin(id: string): Promise<boolean> {
    const response = await RosterBackendService.reactivateAdmin({ id });
    if (response.isOk()) {
        return response.value;
    }
    return false;
}

async function getAllSites(): Promise<SiteDTO[]> {
    const response = await RosterBackendService.getAllSites();
    if (response.isOk()) {
        return response.value;
    } else {
        return [];
    }
}

async function getLastIntegrationTypeAuditLog(siteId: number): Promise<SiteAuditLog | null> {
    try {
        const response = await RosterBackendService.getLastIntegrationTypeAuditLog({ siteId });
        if (response.isErr())
            return null;

        return response.value.log;
    } catch (error) {
        return null
    }
}

async function changeIntegrationType(
    integrationType: SiteIntegrationTypeEnum,
    integrationMethod: SiteIntegrationMethodEnum,
    siteId: number
): Promise<boolean | null> {
    const response = await RosterBackendService.manageChangeIntegrationType({
        siteId: siteId,
        integrationType: integrationType,
        integrationMethod: integrationMethod,
    });
    if (response.isOk()) {
        return response.value;
    } else {
        return false;
    }
}

async function getOrderListForSite(siteId: number): Promise<OrderListItem[] | boolean> {
    const response = await RosterBackendService.getOrderListForSite(siteId);
    if (response.isOk()) {
        //remap the dates according to the local timezone
        response.value.forEach((order: any) => {
            const startDate = new Date(order.startDate);
            const endDate = new Date(order.endDate);
            const dateOffset = endDate.getTimezoneOffset();

            const localStartDate = startDate.getTime() + dateOffset * 60 * 1000;
            const localEndDate = endDate.getTime() + dateOffset * 60 * 1000;
            order.startDate = new Date(localStartDate);
            order.endDate = new Date(localEndDate);
        });
        return response.value;
    } else {
        return false;
    }
}

async function getContentLoginTicket(): Promise<string | null> {
    const response = await RosterBackendService.manageSwitchToContentLoadingApp();
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function getAdminImpersonateTicket(siteId: number, userId: string, impersonateReason: string) {
    try {
        const adminUser = authService.getUser()?.id;
        if (!adminUser) return null;

        const impersonateTicketRequest: AdminImpersonateAuthTicketDTO = {
            adminAccountId: adminUser,
            siteId,
            userId,
            impersonateReason
        }

        const response = await RosterBackendService.manageImpersonateTicket(impersonateTicketRequest);
        if (response.isOk()) {
            return response.value;
        } else {
            return null;
        }
    } catch (error) {
        return null;
    }

}

async function testOneRosterAPIAccess(
    applicationId: string,
    token: string,
    provider: string,
    authenticationMethod: string = '',
    authenticationEndpoint: string = '',
    serviceEndpoint: string
): Promise<boolean | null> {
    const response = await RosterBackendService.testOneRosterSiteIntegrationConnection(applicationId, token, provider, authenticationMethod, authenticationEndpoint, serviceEndpoint);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function testCleverAPIAccess(token: string) {
    const response = await RosterBackendService.manageCleverTestToken(token);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function getAPIAccessData(siteId: number): Promise<ORAPIClientConfigDTO | null> {
    const response = await RosterBackendService.getOneRosterSiteIntegrationEntry(siteId);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function saveAPIAccessData(data: ORAPIClientConfigDTO): Promise<ORAPIClientConfigDTO | null> {
    const response = await RosterBackendService.saveOneRosterSiteIntegrationEntry(data);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function updateAPIAccessData(data: ORAPIClientConfigDTO): Promise<ORAPIClientConfigDTO | null> {
    const response = await RosterBackendService.updateOneRosterSiteIntegrationEntry(data);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function deleteAPIAccessData(siteId: number): Promise<boolean | null> {
    const response = await RosterBackendService.deleteOneRosterSiteIntegrationEntry(siteId);
    if (response.isOk()) {
        return response.value;
    } else {
        return null;
    }
}

async function changeSiteType(siteId: number, siteType: SiteTypes) {
    try {
        const updateResponse = await RosterBackendService.changeSiteType({ siteId, siteType });
        if (updateResponse.isErr())
            return null;

        return updateResponse.value;
    } catch (error) {
        return null
    }
}

async function updatePasswordChangeSettings(siteId: number, allowPasswordChange: boolean, defaultPasswords?: DefaultPasswordDTO) {
    try {
        const updateResponse = await RosterBackendService.updatePasswordChangeSettings({ siteId, allowPasswordChange, defaultPasswords });
        if (updateResponse.isErr())
            return null;

        return updateResponse.value;
    } catch (error) {
        return null
    }
}

async function getDefaultPasswords(siteId: number) {
    try {
        const updateResponse = await RosterBackendService.getSiteCustomizations({ siteId, customizationCodes: [CustomizationCode.DefaultPasswords] });
        if (updateResponse.isErr())
            return null;

        return updateResponse.value.defaultPasswords;
    } catch (error) {
        return null
    }
}

async function getContentAreasForSite(siteId: number) {
    const response = await RosterBackendService.getContentAreasForSite({ siteId });
    if (response.isOk()) {
        return response.value.contentAreas;
    }
}

async function setSiteContentAreas(siteId: number, contentAreaCodes: string[]) {
    return RosterBackendService.setSiteContentAreas({ siteId, contentAreaCodes });
}

async function extendLoginWindow(siteId: number) {
    try {
        const updateResponse = await RosterBackendService.extendLoginWindow({ siteId });
        if (updateResponse.isErr())
            return null;

        return true;
    } catch (error) {
        return null
    }
}


export default {
    addNewAdmin,
    deleteAdmin,
    reactivateAdmin,
    editAdminPassword,
    getAllAdmins,
    getAllSites,
    changeIntegrationType,
    getLastIntegrationTypeAuditLog,
    getOrderListForSite,
    getContentLoginTicket,
    testAPIAccess: testOneRosterAPIAccess,
    getAPIAccessData,
    saveAPIAccessData,
    updateAPIAccessData,
    deleteAPIAccessData,
    changeSiteType,
    testCleverAPIAccess,
    updatePasswordChangeSettings,
    getDefaultPasswords,
    getAdminImpersonateTicket,
    extendLoginWindow,
    getContentAreasForSite,
    setSiteContentAreas
}

